import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import {
  format,
  startOfMonth,
  addMonths,
  subMonths,
  addDays,
  isSameDay,
  isWithinInterval,
  parseISO,
  differenceInDays,
} from 'date-fns';
import {
  getCalendarEvents,
  updateBooking,
  rescheduleCleaningTask,
  rescheduleMaintenanceTask,
  getBooking,
  getCleaningTasks,
  getMaintenanceTasks,
} from '@/lib/api';
import api from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

import { AddMenuItems } from './calendar/AddMenuItems';
import { CalendarToolbar } from './calendar/CalendarToolbar';
import { CalendarDialogs } from './calendar/CalendarDialogs';
import { MonthGrid } from './calendar/MonthGrid';
import { WeekGrid } from './calendar/WeekGrid';
import { MyTasksToggle, useMyTasksFilter, isMineForEvent } from '@/components/MyTasksToggle';
import { useAuth } from '@/contexts/AuthContext';

// Deterministic color palette for the property color-legend
const PROPERTY_PALETTE = [
  '#0EA5E9', '#F59E0B', '#10B981', '#EC4899', '#8B5CF6',
  '#EF4444', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

export const CalendarView = ({ selectedProperty, properties, onRefresh }) => {
  const { user } = useAuth();
  const [onlyMine, setOnlyMine] = useMyTasksFilter();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  // Stable color-per-property (sorted by id so re-orders don't shuffle)
  const propertyColorMap = React.useMemo(() => {
    const sorted = [...(properties || [])].sort(
      (a, b) => (a.id || '').localeCompare(b.id || '')
    );
    const map = {};
    sorted.forEach((p, i) => { map[p.id] = PROPERTY_PALETTE[i % PROPERTY_PALETTE.length]; });
    return map;
  }, [properties]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCalendarEvents(selectedProperty?.id);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handlePrev = () =>
    setCurrentDate(viewMode === 'month' ? subMonths(currentDate, 1) : addDays(currentDate, -7));
  const handleNext = () =>
    setCurrentDate(viewMode === 'month' ? addMonths(currentDate, 1) : addDays(currentDate, 7));
  const handleToday = () => setCurrentDate(new Date());

  const openDialog = (type, date = null, item = null) => {
    setSelectedDate(date);
    setEditingItem(item);
    setDialogType(type);
  };

  const closeDialog = () => {
    setDialogType(null);
    setSelectedDate(null);
    setEditingItem(null);
    fetchEvents();
    if (onRefresh) onRefresh();
  };

  const handleEventClick = (event) => setDetailEvent(event);

  const handleEditEvent = async (event) => {
    setDetailEvent(null);
    try {
      if (event.type === 'booking') {
        const response = await getBooking(event.id);
        openDialog('booking', null, response.data);
      } else if (event.type === 'owner_use') {
        const response = await getBooking(event.id);
        openDialog('owner_use', null, response.data);
      } else if (event.type === 'maintenance' || event.type === 'landscaping') {
        const response = await getMaintenanceTasks(event.property_id);
        const task = response.data.find((t) => t.id === event.id);
        if (task) openDialog(event.type === 'landscaping' ? 'landscaping' : 'maintenance', null, task);
      } else if (event.type === 'cleaning') {
        const response = await getCleaningTasks(event.property_id);
        const task = response.data.find((t) => t.id === event.id);
        if (task) openDialog('cleaning', null, task);
      } else if (event.type === 'renovation') {
        const response = await api.get(`/renovation-projects/${event.id}`);
        openDialog('renovation', null, response.data);
      }
    } catch (_) {
      toast.error('Failed to load event for editing');
    }
  };

  const visibleEvents = useMemo(
    () => (onlyMine ? events.filter((e) => isMineForEvent(e, user?.id)) : events),
    [events, onlyMine, user?.id]
  );

  const mineCount = useMemo(
    () => events.filter((e) => isMineForEvent(e, user?.id)).length,
    [events, user?.id]
  );

  const getEventsForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return visibleEvents.filter((event) => {
      if (event.type === 'booking' || event.type === 'owner_use' || event.type === 'renovation') {
        if (!event.end_date) return event.start_date === dateStr;
        try {
          const start = parseISO(event.start_date);
          const end = parseISO(event.end_date);
          return (
            isWithinInterval(date, { start, end }) ||
            isSameDay(date, start) ||
            isSameDay(date, end)
          );
        } catch {
          return false;
        }
      }
      return event.start_date === dateStr;
    });
  }, [visibleEvents]);

  // ---- Drag & Drop ----
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event.id);
    const dragElement = e.target.cloneNode(true);
    dragElement.style.opacity = '0.8';
    dragElement.style.position = 'absolute';
    dragElement.style.top = '-1000px';
    document.body.appendChild(dragElement);
    e.dataTransfer.setDragImage(dragElement, 0, 0);
    setTimeout(() => document.body.removeChild(dragElement), 0);
  };
  const handleDragEnd = () => { setDraggedEvent(null); setDragOverDate(null); };
  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dateStr = format(date, 'yyyy-MM-dd');
    if (dragOverDate !== dateStr) setDragOverDate(dateStr);
  };
  const handleDragLeave = () => setDragOverDate(null);

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    setDragOverDate(null);
    if (!draggedEvent) return;
    const targetDateStr = format(targetDate, 'yyyy-MM-dd');
    try {
      if (draggedEvent.type === 'booking' || draggedEvent.type === 'owner_use') {
        const originalStart = parseISO(draggedEvent.start_date);
        const originalEnd = parseISO(draggedEvent.end_date);
        const duration = differenceInDays(originalEnd, originalStart);
        await updateBooking(draggedEvent.id, {
          check_in: targetDateStr,
          check_out: format(addDays(targetDate, duration), 'yyyy-MM-dd'),
        });
        toast.success(
          `${draggedEvent.type === 'owner_use' ? 'Owner block' : 'Booking'} moved to ${format(targetDate, 'MMM d')}`
        );
      } else if (draggedEvent.type === 'cleaning') {
        await rescheduleCleaningTask(draggedEvent.id, targetDateStr);
        toast.success(`Cleaning task moved to ${format(targetDate, 'MMM d, yyyy')}`);
      } else if (draggedEvent.type === 'maintenance' || draggedEvent.type === 'landscaping') {
        await rescheduleMaintenanceTask(draggedEvent.id, targetDateStr);
        toast.success(
          `${draggedEvent.type === 'landscaping' ? 'Landscaping' : 'Maintenance'} task moved to ${format(targetDate, 'MMM d, yyyy')}`
        );
      } else if (draggedEvent.type === 'renovation') {
        await api.put(`/renovation-projects/${draggedEvent.id}`, { start_date: targetDateStr });
        toast.success(`Renovation start moved to ${format(targetDate, 'MMM d, yyyy')}`);
      }
      fetchEvents();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error rescheduling event:', error);
      toast.error(error.response?.data?.detail || 'Failed to reschedule event');
    }
    setDraggedEvent(null);
  };

  // Handler shared by MonthGrid and WeekGrid for day-cell picks
  const handleDayPickType = (date, type) => {
    setSelectedDate(date);
    openDialog(type, date);
  };

  const gridProps = {
    currentDate,
    getEventsForDate,
    propertyColorMap,
    dragOverDate,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onEventClick: handleEventClick,
    onCellClick: setSelectedDate,
    onDayPickType: handleDayPickType,
    showPropertyName: !selectedProperty,
  };

  return (
    <div className="space-y-6" data-testid="calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Calendar
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - {format(currentDate, 'MMMM yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MyTasksToggle
            enabled={onlyMine}
            onChange={setOnlyMine}
            count={mineCount}
            testId="calendar-my-tasks-toggle"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-event-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <AddMenuItems onPick={(type) => openDialog(type)} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CalendarToolbar
        currentDate={currentDate}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onPrev={handlePrev}
        onToday={handleToday}
        onNext={handleNext}
        properties={properties}
        selectedProperty={selectedProperty}
        propertyColorMap={propertyColorMap}
      />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : viewMode === 'month' ? (
        <MonthGrid {...gridProps} />
      ) : (
        <WeekGrid {...gridProps} />
      )}

      <CalendarDialogs
        dialogType={dialogType}
        onClose={closeDialog}
        selectedDate={selectedDate}
        editingItem={editingItem}
        properties={properties}
        selectedProperty={selectedProperty}
        detailEvent={detailEvent}
        onDetailClose={(changed) => {
          setDetailEvent(null);
          if (changed) {
            fetchEvents();
            if (onRefresh) onRefresh();
          }
        }}
        onEditEvent={handleEditEvent}
      />
    </div>
  );
};

export default CalendarView;
